import { useOffer } from '@/providers/PriceFreezeOfferProvider';
import { selectOfferDuration } from '@/selectors';
import {
  Desktop,
  Mobile,
  useFlow,
  useTrackPage,
  useTranslation,
} from '@edreamsodigeo/frontend-core';
import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import React, { useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  BUYER_INFO,
  IS_ACTIVE_PRIME_USER,
  PF_AUTO_RENEWAL,
  usePriceFreezeSession,
} from '@/hooks/usePriceFreezeSession';
import { useLogin } from '@/providers/login/LoginProvider';
import { useContactDetails } from '@/providers/UserInfoProvider';
import { useIsActivePrimeUser } from '@/providers/PrimeUserProvider';
import { SearchExpiredError } from '@/components/FunnelErrors';
import { VersionedPrimeInfoBanner } from '@/components/PrimeInfoBanner/PFPrimeInfoBanner';
import { Box, Flex } from '@edreamsodigeo/prisma-design-system';
import { VersionedIRUMPrimeHeader } from '@/components/HomePage/HomePagePrimeHeader';
import { PriceOffer } from '@/components/PriceOffer';
import {
  ItineraryDetailsIRUM,
  StyledNoFeeKey,
} from '@/components/HomePage/HomePageUtilsIRUM';
import { SummaryContactDetails } from '@/components/SummaryContactDetails';
import { MoreInfoLink } from '@/components/MoreInfoLink';
import {
  VersionedHomeContactDetailsButton,
  VersionedHomeContactDetailsButtonDesktop,
} from '@/components/HomePage/HomePageButtonContactDetails';
import { AutorenewalDrawer } from '@/components/AutorenewalDrawer';
import { withLoadPlaceholder } from '@/HOC/withDatamesh';
import { useUserMembershipQuery } from '@/hooks/GQL';
import { featuredAutoRenewalOnInDeposit } from '@/featuredComponents';

const DatameshTrackLoad = withLoadPlaceholder('home:load');

const MobileContactDetailsButton = ({ loading, onContinueHandler }) => (
  <VersionedHomeContactDetailsButton
    loading={loading}
    onContinueHandler={onContinueHandler}
  />
);

const MobileContactDetailsButtonWithAutorenewal = ({
  loading,
  isPrime,
  onContinueHandler,
  onContinueWithAutorenewalHandler,
}) => (
  <VersionedHomeContactDetailsButton
    loading={loading}
    onContinueHandler={
      !isPrime ? onContinueHandler : onContinueWithAutorenewalHandler
    }
  />
);

const ContactDetailsButton = featuredAutoRenewalOnInDeposit([
  MobileContactDetailsButton,
  MobileContactDetailsButtonWithAutorenewal,
]);

export function HomepageComponent({
  formFields,
  loading,
  error,
  validation = () => true,
  onContinue,
  onContinueWithAutorenewal,
}) {
  const offer = useOffer();
  const days = selectOfferDuration(offer);
  const { t } = useTranslation();
  const { track } = usePFTracking();
  const { trackGAPage, trackTMSPage } = useTrackPage();
  const capValue = offer.cap;
  const flow = useFlow();

  const PrimeHeaderPaddingFromLogoValue =
    flow === 'desktop' ? '120px' : '1x_huge';

  useEffect(() => {
    trackGAPage('/BF/price-freeze/summary-details-extras/');
    trackTMSPage({ page_name: 'summary', env_template: 'summary' });
  }, [trackGAPage, trackTMSPage]);

  useDocumentTitle(t('header.title.pricefreeze'));
  const [getSessionItem, setSessionItem] = usePriceFreezeSession();
  const buyerSessionData = getSessionItem(BUYER_INFO);
  const { user, isLogged, isPrime } = useLogin();
  const contactDetails = useContactDetails();
  const [data, setData] = useState(
    () => contactDetails || buyerSessionData || user || {}
  );
  const contactDetailsFormRef = useRef(null);

  const [showErrors, setShowErrors] = useState(false);
  const {
    isActive,
    setUserEmail,
    userEmail = '',
    loading: isActiveLoading,
  } = useIsActivePrimeUser();

  const { data: userMembershipData, loading: userMembershipLoading } =
    useUserMembershipQuery();
  const membershipExpirationDate =
    userMembershipData?.userAccount?.userMembership?.subscriptionDetails
      ?.expirationDate;
  const isAutorenewalStatusEnabled =
    userMembershipData?.userAccount?.userMembership?.subscriptionDetails
      ?.isAutoRenewalEnabled;
  const [showRenewalDrawer, setShowRenewalDrawer] = useState(false);

  const onBlur = (id) => (val) => {
    setData({ ...data, [id]: val });
    if (id === formFields.EMAIL.id) {
      setActiveUserEmail(id, val);
    }
  };
  const setActiveUserEmail = (id, val) => {
    if (val !== userEmail) {
      setUserEmail(val);
    }
  };
  const validate = () =>
    Object.values(formFields).every(({ id, validation }) => {
      return validation(data[id]);
    });

  const showInvalidForm = () => {
    setShowErrors(true);
    if (contactDetailsFormRef?.current) {
      window.scrollTo({
        top: contactDetailsFormRef.current?.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const isFormValid = () => {
    if (!validate() || !validation()) {
      showInvalidForm();
    } else {
      return true;
    }
  };

  const onContinueButtonClick = () => {
    if (isFormValid()) {
      track(ACTIONS.HOME.CLICK.CONTINUE);
      window.onbeforeunload = null;
      setSessionItem(BUYER_INFO, data);
      setSessionItem(IS_ACTIVE_PRIME_USER, isActive);
      onContinue(data);
    }
  };

  const onContinueAutorenewalClick = () => {
    if (isFormValid()) {
      track(ACTIONS.HOME.CLICK.CONTINUE);
      window.onbeforeunload = null;
      setSessionItem(BUYER_INFO, data);
      setSessionItem(IS_ACTIVE_PRIME_USER, isActive);
      //Show autorenewal drawer only if the user has autorenewal disabled and no previous choice stored in the session
      //PF_AUTO_RENEWAL is set when the drawer shows up for the first time
      if (
        isAutorenewalStatusEnabled === false &&
        getSessionItem(PF_AUTO_RENEWAL) === undefined
      ) {
        setShowRenewalDrawer(true);
      } else {
        onContinue(data);
      }
    }
  };

  return (
    <>
      <DatameshTrackLoad />
      <SearchExpiredError open={error} />
      {!isLogged && <VersionedPrimeInfoBanner />}
      <Box backgroundColor="white" pb="2x_big" data-testid="homepage">
        {showRenewalDrawer && isPrime && (
          <Mobile>
            <AutorenewalDrawer
              showRenewalDrawer={showRenewalDrawer}
              setShowRenewalDrawer={setShowRenewalDrawer}
              depositPrice={offer?.deposit?.amount}
              currency={offer?.deposit?.currency}
              subscriptionExpirationDate={membershipExpirationDate}
              onPrimaryAction={() => onContinueWithAutorenewal(data)}
              onSecondaryAction={() => onContinue(data)}
            />
          </Mobile>
        )}

        <VersionedIRUMPrimeHeader
          PrimeHeaderPaddingFromLogoValue={PrimeHeaderPaddingFromLogoValue}
          days={days}
        />

        <LayoutByInterface
          offer={<PriceOffer noFeeKey={<StyledNoFeeKey />} />}
          contactDetails={
            <div ref={contactDetailsFormRef}>
              <SummaryContactDetails
                data={data}
                onBlur={onBlur}
                showErrors={showErrors}
                isActive={isActive}
                userEmail={userEmail}
                isActiveLoading={isActiveLoading}
                disabledEmail={
                  Boolean(contactDetails?.email) || Boolean(user?.email)
                }
                formFields={formFields}
              />
            </div>
          }
        />

        <Mobile>
          <ItineraryDetailsIRUM />
        </Mobile>
      </Box>
      <Flex mt="big" mb="2x_big" justifyContent="center">
        <MoreInfoLink cap={capValue} deposit={offer.deposit} days={days} />
      </Flex>
      <Mobile>
        <ContactDetailsButton
          isPrime={isPrime}
          loading={loading || userMembershipLoading}
          onContinueHandler={onContinueButtonClick}
          onContinueWithAutorenewalHandler={onContinueAutorenewalClick}
        />
      </Mobile>
      <Desktop>
        <VersionedHomeContactDetailsButtonDesktop
          loading={loading}
          disabled={!isActive && !isLogged}
          onContinueHandler={onContinueButtonClick}
        />
      </Desktop>
    </>
  );
}

const LayoutByInterface = ({ offer, contactDetails }) => (
  <>
    <Mobile>
      <Box px="base">{offer}</Box>
      {contactDetails}
    </Mobile>
    <Desktop>
      <Flex>
        <Box ml="2x_big" mt="small" flex={1.7}>
          {offer}
        </Box>
        <Box flex={2}>{contactDetails}</Box>
      </Flex>
    </Desktop>
  </>
);
